import { SamplingJobChartType, type SamplingMetricsChartProps } from './sampling-metrics-chart.types.ts';
import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  type TooltipContext,
} from '@progress/kendo-react-charts';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SamplingJobMetricChartModel } from '../../pages/sampling-metrics-page.types.ts';
import styles from './sampling-metrics-chart.module.scss';

export function SamplingMetricsChart({ className, chartType, interactive, data }: SamplingMetricsChartProps) {
  const chartRef = useRef(null);

  const [chartKey, setChartKey] = useState<number>(0);

  useEffect(() => {
    if (!chartRef.current) return;

    const resizeObserver = new ResizeObserver(() => setChartKey((prev) => prev + 1));

    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const chartData = useMemo(() => {
    return Object.values(
      data
        .filter((metric) => metric.sampleCount > 0)
        .reduce<Record<string, SamplingJobMetricChartModel>>((acc, metric) => {
          if (chartType === SamplingJobChartType.Sampler) {
            const samplerName = metric.sampler ?? 'Unassigned';
            const compositeKey = `${samplerName}_${metric.plannedDate}`;

            if (!acc[compositeKey]) {
              acc[compositeKey] = {
                title: samplerName,
                date: metric.plannedDate,
                count: 0,
                colour: !metric.sampler ? '#a3a3a3' : undefined,
              };
            }

            acc[compositeKey].count += metric.sampleCount || 0;
          } else if (chartType === SamplingJobChartType.JobStatus) {
            const compositeKey = `${metric.jobStatus}_${metric.plannedDate}`;

            if (!acc[compositeKey]) {
              acc[compositeKey] = {
                title: metric.jobStatus,
                date: metric.plannedDate,
                count: 0,
              };
            }

            acc[compositeKey].count += metric.sampleCount || 0;
          } else if (chartType === SamplingJobChartType.SampleType) {
            const compositeKey = `${metric.sampleType}_${metric.plannedDate}`;

            if (!acc[compositeKey]) {
              acc[compositeKey] = {
                title: metric.sampleType,
                date: metric.plannedDate,
                count: 0,
              };
            }

            acc[compositeKey].count += metric.sampleCount || 0;
          } else {
            const compositeKey = `${metric.sampled}_${metric.plannedDate}`;

            if (!acc[compositeKey]) {
              acc[compositeKey] = {
                title: metric.sampled ? 'Samples Taken' : 'Samples Planned',
                date: metric.plannedDate,
                count: 0,
              };
            }

            acc[compositeKey].count += metric.sampleCount;
          }

          return acc;
        }, {})
    );
  }, [chartType, data]);

  const labelStepDate = useMemo(() => {
    if (data.length === 0) {
      return 1;
    }

    const startTime = new Date(data[0].plannedDate).getTime();
    const endTime = new Date(data[data.length - 1].plannedDate).getTime();

    const weeks = Math.floor(Math.abs(endTime - startTime) / 604_800_000);

    return Math.max(1, Math.ceil(weeks / 26));
  }, [data]);

  return (
    <div ref={chartRef} className={className}>
      <Chart key={chartKey} pannable={interactive} zoomable={interactive}>
        <ChartLegend position={'bottom'} orientation={'horizontal'} margin={{ top: 16 }} />
        <ChartTooltip
          render={({ point }: TooltipContext) => (
            <div className={styles.tooltip}>
              <strong>{point.series.name}</strong>: {point.value?.toLocaleString()} samples
            </div>
          )}
        />
        <ChartArea />
        <ChartValueAxis>
          <ChartValueAxisItem title={{ text: 'Sample Counts' }} line={{ visible: false }} labels={{ format: '0' }} />
        </ChartValueAxis>
        <ChartCategoryAxis>
          <ChartCategoryAxisItem
            type={'date'}
            baseUnit={'weeks'}
            baseUnitStep={1}
            weekStartDay={1}
            labels={{
              format: 'dd MMM',
              step: labelStepDate,
              rotation: -45,
            }}
          />
        </ChartCategoryAxis>
        <ChartSeries>
          {Array.from(new Set(chartData.map((item) => item.title))).map((title) => (
            <ChartSeriesItem
              key={title}
              type={'column'}
              stack={true}
              name={title}
              gap={0.5}
              spacing={0.2}
              border={{ width: 0 }}
              data={chartData.filter((item) => item.title === title)}
              categoryField={'date'}
              field={'count'}
              color={chartData.find((item) => item.title === title)!.colour}
            />
          ))}
        </ChartSeries>
      </Chart>
    </div>
  );
}
